import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import type { AdminAuditLog, CuriousQuestion, ScholarshipApplication } from "@mhj/shared/types";

type Summary = {
  totalApplications: number;
  submitted: number;
  underReview: number;
  approved: number;
  disbursed: number;
  rejected: number;
  pendingQna: number;
};

export default function AdminDashboard() {
  const { data, error } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const [applications, curious, audit] = await Promise.all([
        apiRequest<{ applications: ScholarshipApplication[] }>("/admin/scholarships"),
        apiRequest<{ questions: CuriousQuestion[] }>("/admin/curious"),
        apiRequest<{ logs: AdminAuditLog[] }>("/admin/audit"),
      ]);

      const totalApplications = applications.applications.length;
      const submitted = applications.applications.filter(
        (item) => item.status === "submitted"
      ).length;
      const underReview = applications.applications.filter(
        (item) => item.status === "under_review"
      ).length;
      const approved = applications.applications.filter(
        (item) => item.status === "approved"
      ).length;
      const disbursed = applications.applications.filter(
        (item) => item.status === "disbursed"
      ).length;
      const rejected = applications.applications.filter(
        (item) => item.status === "rejected"
      ).length;
      const pendingQna = curious.questions.filter(
        (item) => item.status === "pending"
      ).length;

      return {
        summary: {
          totalApplications,
          submitted,
          underReview,
          approved,
          disbursed,
          rejected,
          pendingQna,
        },
        recentLogs: audit.logs.slice(0, 6),
      };
    },
  });

  const summary = data?.summary ?? null;
  const recentLogs = data?.recentLogs ?? [];

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Admin dashboard
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-black">
          Operational overview
        </h2>
        <p className="mt-2 text-sm text-black/60">
          Monitor applications, Q&amp;A moderation, and audit activity.
        </p>
        {summary ? (
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              ["Total applications", summary.totalApplications],
              ["Submitted", summary.submitted],
              ["Under review", summary.underReview],
              ["Approved", summary.approved],
              ["Disbursed", summary.disbursed],
              ["Rejected", summary.rejected],
              ["Pending Q&A", summary.pendingQna]
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-black/10 bg-white/50 p-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-black/50">
                  {label}
                </p>
                <p className="mt-3 text-2xl font-semibold text-primary-500">
                  {value}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-6 text-sm text-black/60">Loading summary…</p>
        )}
      </div>
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-400">
          Recent admin actions
        </p>
        <div className="mt-4 space-y-3 text-sm text-black/70">
          {recentLogs.length === 0 ? (
            <p>No audit logs yet.</p>
          ) : (
            recentLogs.map((log) => (
              <div
                key={log.id}
                className="rounded-2xl border border-black/10 bg-white/50 px-4 py-3"
              >
                <p className="text-black">
                  {log.action} — {log.entity} {log.entityId ? `(${log.entityId})` : ""}
                </p>
                <p className="text-xs text-black/40">{log.createdAt}</p>
              </div>
            ))
          )}
        </div>
      </div>
     
    </div>
  );
}
